// Сметките на справка „Разделени маси": редовете на таблицата, обобщението и
// точките на графиката с активността.
//
// Числата са ЕДНО КЪМ ЕДНО с досегашния екран — брой разделяния, брой
// прехвърлени продукти (сборът от дължините на `items`), обща прехвърлена
// стойност и средното на едно разделяне. Тук нищо не се преизчислява; същите
// сборове само се събират на едно място, вместо да живеят в маркъпа.

import dayjs, { type Dayjs } from "dayjs";

import { parseDecimal } from "../../../../components/dataTable";
import type { ChecklistOption } from "../../../../components/dataTable";
import type { BillSplit } from "../../../../api/reportsBillsSplits";
import { businessDateOf } from "../../../../lib/businessDay";

/** Ред в таблицата: записът от BE плюс полетата, които колоните четат направо. */
export type SplitRow = BillSplit & {
  /** Името на сервитьора или празен низ — клетката рисува тирето. */
  waiterLabel: string;
  itemsCount: number;
  /** `total_amount` като ЧИСЛО — Decimal полетата от BE идват като низ. */
  amount: number;
};

export type SplitsTotals = {
  splits: number;
  products: number;
  waiters: number;
  amount: number;
  /** Средно на едно разделяне; 0 при нула разделяния (виж `splits`). */
  average: number;
};

export const buildSplitRows = (splits: BillSplit[]): SplitRow[] =>
  splits.map((split) => ({
    ...split,
    waiterLabel: split.waiter_name ?? "",
    itemsCount: split.items.length,
    amount: parseDecimal(split.total_amount),
  }));

export const summarizeSplits = (rows: SplitRow[]): SplitsTotals => {
  const splits = rows.length;
  const products = rows.reduce((sum, row) => sum + row.itemsCount, 0);
  const amount = rows.reduce((sum, row) => sum + row.amount, 0);
  // Сервитьорите се броят по УНИКАЛНО ИМЕ, а записите без сервитьор не влизат
  // — точно както досега.
  const waiters = new Set(
    rows.map((row) => row.waiterLabel).filter(Boolean),
  ).size;
  return {
    splits,
    products,
    waiters,
    amount,
    average: splits > 0 ? amount / splits : 0,
  };
};

/**
 * Опциите за чеклист филтъра в колона. Подават се ИЗРИЧНО, макар режимът да е
 * клиентски: така подредбата е азбучна и стабилна, вместо да следва реда, в
 * който стойностите са се появили в отговора.
 */
export const checklistOptionsOf = (
  rows: SplitRow[],
  pick: (row: SplitRow) => string,
): ChecklistOption[] => {
  const seen = new Set<string>();
  for (const row of rows) {
    const value = pick(row);
    if (value) seen.add(value);
  }
  return Array.from(seen)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .map((value) => ({ value, label: value }));
};

/* ─────────────────────────── активност по време ─────────────────────────── */

export type ActivityPoint = { key: string; label: string; count: number };

/** Таванът на дневните стълбчета — както досега, по-дълъг период се подрязва. */
const MAX_DAYS = 62;

/**
 * Периодът е един ден → графиката е по ЧАС, иначе по ДЕН.
 *
 * Сравнението е по РАБОТНА дата: работният ден свършва в 05:59 на следващата
 * календарна дата и суровото `isSame(…, "day")` би обявило „днес" за период.
 */
export const isSingleDayRange = (
  range: [Dayjs | null, Dayjs | null] | null,
): boolean => {
  const start = range?.[0];
  const end = range?.[1];
  return Boolean(
    start && end && businessDateOf(start).isSame(businessDateOf(end), "day"),
  );
};

/**
 * Стълбчетата на графиката. При един ден — 24 кофи по локален час на
 * `split_date`; при повече — по една на ден от периода.
 */
export const buildActivityPoints = (
  rows: SplitRow[],
  range: [Dayjs | null, Dayjs | null] | null,
  isSingleDay: boolean,
): ActivityPoint[] => {
  const start = range?.[0];
  const end = range?.[1];
  if (!start || !end) return [];

  if (isSingleDay) {
    const hours: ActivityPoint[] = Array.from({ length: 24 }, (_, hour) => ({
      key: String(hour).padStart(2, "0"),
      label: String(hour).padStart(2, "0"),
      count: 0,
    }));
    for (const row of rows) {
      // През `dayjs`, както и дневните кофи: `new Date(…).getHours()` дава
      // NaN при непарсваем час и индексът NaN би хвърлил на място.
      const moment = dayjs(row.split_date);
      if (moment.isValid()) hours[moment.hour()].count++;
    }
    return hours;
  }

  // Кофите са по РАБОТНА дата — иначе разделяне в 00:20 пада в кофата на
  // следващия ден, а работният ден се разделя на две колонки.
  const firstDay = businessDateOf(start);
  const lastDay = businessDateOf(end);
  const days = Math.min(lastDay.diff(firstDay, "day") + 1, MAX_DAYS);
  const byDay = new Map<string, ActivityPoint>();
  for (let index = 0; index < days; index++) {
    const day = firstDay.add(index, "day");
    const key = day.format("YYYY-MM-DD");
    byDay.set(key, { key, label: day.format("DD.MM"), count: 0 });
  }
  for (const row of rows) {
    const moment = dayjs(row.split_date);
    if (!moment.isValid()) continue;
    const bucket = byDay.get(businessDateOf(moment).format("YYYY-MM-DD"));
    if (bucket) bucket.count++;
  }
  return Array.from(byDay.values());
};

/** През колко стълбчета се изписва етикет, за да не се слепят надписите. */
export const activityLabelStep = (
  pointCount: number,
  isHourly: boolean,
): number => {
  if (pointCount <= 10) return 1;
  if (pointCount <= 16) return 2;
  if (pointCount <= 24) return isHourly ? 3 : 2;
  return 4;
};
